import { useMemo, useState, useCallback } from 'react'
import { Link } from 'react-router-dom'
import { useCachedQuery } from '@/hooks/useCachedQuery'
import type { MovimientoCaja, Factura, PagoProgramado, PagoRecurrente } from '@/lib/types'
import { formatCurrency, formatDate, r2 } from '@/lib/utils'
import { PageHeader } from '@/components/ui'
import { IconArrowDown, IconArrowUp, IconChevronRight, IconPlus, IconRefresh } from '@/components/icons/Icons'

/* ─── Constants ─── */

const MONTH_LABELS = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic']

const CATEGORY_COLORS: Record<string, string> = {
  'Infraestructura Cloud': '#3b82f6',
  'Herramientas y Licencias': '#8b5cf6',
  'Marketing': '#f59e0b',
  'Operaciones': '#10b981',
}
const CATEGORY_FALLBACK = '#6b7280'
const TC_REF = 3.75  // Tipo de cambio referencial USD→PEN

const CRUMBS = [{ label: 'Contabilidad' }]

/* ─── Helpers ─── */

/** Parse YYYY-MM-DD without timezone offset */
function parseDateParts(dateStr: string): [number, number, number] {
  const [y, m, d] = dateStr.split('-').map(Number)
  return [y, m, d]
}

function isSameMonth(dateStr: string, year: number, month: number): boolean {
  const [y, m] = parseDateParts(dateStr)
  return y === year && (m - 1) === month // month is 0-indexed from caller
}

/** Format large numbers as compact (e.g. $1.7K, $12.4K) */
function formatShort(n: number, moneda: 'USD' | 'PEN'): string {
  const sym = moneda === 'USD' ? '$' : 'S/'
  const abs = Math.abs(n)
  if (abs >= 1_000_000) return `${sym}${(n / 1_000_000).toFixed(1)}M`
  if (abs >= 1_000) return `${sym}${(n / 1_000).toFixed(1)}K`
  return `${sym}${n.toFixed(0)}`
}



/* ─── Chart Tooltip Type ─── */
interface TooltipData {
  label: string
  ingresosUSD: number
  egresosUSD: number
  ingresosPEN: number
  egresosPEN: number
  ingresosTotal: number   // consolidated PEN
  egresosTotal: number    // consolidated PEN
  saldoNeto: number       // ingresos - egresos consolidated
  ingresosProyectados: number  // pagos_programados pendientes (consolidado PEN)
  egresosProyectados: number
  saldoProyectado: number      // (real + proyectado) ingresos - egresos
  x: number
  y: number
}

/* ═════════════════════════════════════════════════════════════════════
   COMPONENT
   ═════════════════════════════════════════════════════════════════════ */

export default function DashboardContabilidad() {
  const { data: movimientos = [], isLoading: l1 } = useCachedQuery<MovimientoCaja>('movimientos_caja')
  const { data: facturas = [], isLoading: l2 } = useCachedQuery<Factura>('facturas')
  const { data: programados = [], isLoading: l3 } = useCachedQuery<PagoProgramado>('pagos_programados')
  const { data: recurrentes = [], isLoading: l4 } = useCachedQuery<PagoRecurrente>('pagos_recurrentes')


  const [tooltip, setTooltip] = useState<TooltipData | null>(null)
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear())

  const loading = l1 || l2 || l3 || l4
  const now = new Date()
  const currentYear = selectedYear

  /* ─── Años disponibles ─── */
  // Incluye los años de lo PROYECTADO, no solo los de la caja real: el gráfico
  // dibuja "Ing./Egr. Proyectado" y sin esto un cronograma a futuro (p. ej. un
  // fraccionamiento SUNAT a 2027) no tiene año que seleccionar y queda invisible.
  const availableYears = useMemo(() => {
    const years = new Set(movimientos.map(m => parseDateParts(m.fecha)[0]))
    for (const p of programados) {
      const f = p.fecha_vencimiento || p.fecha_programada
      if (f) years.add(parseDateParts(f)[0])
    }
    years.add(new Date().getFullYear()) // always include current
    return Array.from(years).sort((a, b) => b - a)
  }, [movimientos, programados])



  /* ─── KPIs del mes actual ─── */
  const kpis = useMemo(() => {
    const mesActual = movimientos.filter(m => isSameMonth(m.fecha, currentYear, now.getMonth()))

    const byMoneda = (moneda: string) => {
      const movs = mesActual.filter(m => m.moneda === moneda)
      const ingresos = r2(movs.filter(m => m.tipo === 'ingreso').reduce((s, m) => s + m.monto, 0))
      const egresos = r2(movs.filter(m => m.tipo === 'egreso').reduce((s, m) => s + m.monto, 0))
      const base_ing = r2(movs.filter(m => m.tipo === 'ingreso').reduce((s, m) => s + (m.base_imponible || 0), 0))
      const igv_ing = r2(movs.filter(m => m.tipo === 'ingreso').reduce((s, m) => s + (m.igv || 0), 0))
      return { ingresos, egresos, saldo: r2(ingresos - egresos), base_ing, igv_ing }
    }

    const pen = byMoneda('PEN')
    const usd = byMoneda('USD')

    const facturasPendientes = facturas.filter(f => f.estado === 'emitida' || f.estado === 'pre_factura')
    const totalPendientePEN = r2(facturasPendientes.filter(f => f.moneda === 'PEN').reduce((s, f) => s + f.total, 0))
    const totalPendienteUSD = r2(facturasPendientes.filter(f => f.moneda === 'USD').reduce((s, f) => s + f.total, 0))

    // Ingreso promedio mensual consolidado PEN (hasta la fecha)
    const mesesTranscurridos = currentYear === now.getFullYear() ? now.getMonth() + 1 : 12
    const ingAnual = movimientos.filter(m => parseDateParts(m.fecha)[0] === currentYear && m.tipo === 'ingreso')
    const totalIngConsolidado = r2(ingAnual.reduce((s, m) => s + (m.moneda === 'USD' ? r2(m.monto * TC_REF) : m.monto), 0))
    const ingresoPromedio = r2(totalIngConsolidado / mesesTranscurridos)

    return { pen, usd, facturasPendientes: facturasPendientes.length, totalPendientePEN, totalPendienteUSD, ingresoPromedio, totalIngConsolidado, mesesTranscurridos }
  }, [movimientos, facturas])

  /* ─── KPIs tributarios acumulados (todo el año) ─── */
  const tributario = useMemo(() => {
    const anual = movimientos.filter(m => parseDateParts(m.fecha)[0] === currentYear)

    const byMoneda = (moneda: string) => {
      const movs = anual.filter(m => m.moneda === moneda)
      const igv_cobrado = r2(movs.filter(m => m.tipo === 'ingreso').reduce((s, m) => s + (m.igv || 0), 0))
      const igv_pagado = r2(movs.filter(m => m.tipo === 'egreso').reduce((s, m) => s + (m.igv || 0), 0))
      const detracciones = r2(movs.filter(m => m.tiene_detraccion).reduce((s, m) => s + (m.monto_detraccion || 0), 0))
      return { igv_cobrado, igv_pagado, igv_neto: r2(igv_cobrado - igv_pagado), detracciones }
    }

    return { pen: byMoneda('PEN'), usd: byMoneda('USD') }
  }, [movimientos])

  /* ─── 12-Month Stacked Bar Chart Data (consolidated PEN) ─── */
  const chartData = useMemo(() => {
    const months: {
      label: string
      ingresosUSD: number; egresosUSD: number
      ingresosPEN: number; egresosPEN: number
      ingresosUSDpen: number; egresosUSDpen: number
      ingresosTotal: number; egresosTotal: number
      saldoNeto: number
      egresosProyectados: number
      ingresosProyectados: number
      saldoProyectado: number
    }[] = []
    for (let m = 0; m < 12; m++) {
      const label = MONTH_LABELS[m]
      const mes = movimientos.filter(mv => isSameMonth(mv.fecha, currentYear, m))
      const ingresosUSD = r2(mes.filter(mv => mv.tipo === 'ingreso' && mv.moneda === 'USD').reduce((s, mv) => s + mv.monto, 0))
      const egresosUSD = r2(mes.filter(mv => mv.tipo === 'egreso' && mv.moneda === 'USD').reduce((s, mv) => s + mv.monto, 0))
      const ingresosPEN = r2(mes.filter(mv => mv.tipo === 'ingreso' && mv.moneda === 'PEN').reduce((s, mv) => s + mv.monto, 0))
      const egresosPEN = r2(mes.filter(mv => mv.tipo === 'egreso' && mv.moneda === 'PEN').reduce((s, mv) => s + mv.monto, 0))
      const ingresosUSDpen = r2(ingresosUSD * TC_REF)
      const egresosUSDpen = r2(egresosUSD * TC_REF)
      const ingresosTotal = r2(ingresosPEN + ingresosUSDpen)
      const egresosTotal = r2(egresosPEN + egresosUSDpen)
      const saldoNeto = r2(ingresosTotal - egresosTotal)
      // Projected egresos from pagos_programados
      const proyEgrPEN = r2(programados.filter(p => {
        const [py, pm] = parseDateParts(p.fecha_vencimiento)
        return py === currentYear && (pm - 1) === m && p.estado === 'pendiente' && p.tipo === 'egreso' && p.moneda === 'PEN'
      }).reduce((s, p) => s + p.monto, 0))
      const proyEgrUSD = r2(programados.filter(p => {
        const [py, pm] = parseDateParts(p.fecha_vencimiento)
        return py === currentYear && (pm - 1) === m && p.estado === 'pendiente' && p.tipo === 'egreso' && p.moneda === 'USD'
      }).reduce((s, p) => s + p.monto, 0))
      const egresosProyectados = r2(proyEgrPEN + proyEgrUSD * TC_REF)
      // Projected ingresos from pagos_programados
      const proyIngPEN = r2(programados.filter(p => {
        const [py, pm] = parseDateParts(p.fecha_vencimiento)
        return py === currentYear && (pm - 1) === m && p.estado === 'pendiente' && p.tipo === 'ingreso' && p.moneda === 'PEN'
      }).reduce((s, p) => s + p.monto, 0))
      const proyIngUSD = r2(programados.filter(p => {
        const [py, pm] = parseDateParts(p.fecha_vencimiento)
        return py === currentYear && (pm - 1) === m && p.estado === 'pendiente' && p.tipo === 'ingreso' && p.moneda === 'USD'
      }).reduce((s, p) => s + p.monto, 0))
      const ingresosProyectados = r2(proyIngPEN + proyIngUSD * TC_REF)
      // Saldo si lo programado se cumple: la linea de saldo real cae a cero en los
      // meses futuros aunque haya cronograma, y asi el proyectado no se ve en el total.
      const saldoProyectado = r2((ingresosTotal + ingresosProyectados) - (egresosTotal + egresosProyectados))
      months.push({ label, ingresosUSD, egresosUSD, ingresosPEN, egresosPEN, ingresosUSDpen, egresosUSDpen, ingresosTotal, egresosTotal, saldoNeto, egresosProyectados, ingresosProyectados, saldoProyectado })
    }
    const maxVal = Math.max(
      ...months.map(m => m.ingresosTotal + m.ingresosProyectados),
      ...months.map(m => m.egresosTotal + m.egresosProyectados),
      100
    )
    return { months, maxVal }
  }, [movimientos, currentYear, programados])

  /* ─── Category Breakdown (consolidated PEN) ─── */
  const categoryData = useMemo(() => {
    const anual = movimientos.filter(
      m => parseDateParts(m.fecha)[0] === currentYear && m.tipo === 'egreso'
    )
    const catMap: Record<string, number> = {}
    for (const m of anual) {
      const cat = m.categoria || 'Otros'
      const pen = m.moneda === 'USD' ? r2(m.monto * TC_REF) : m.monto
      catMap[cat] = r2((catMap[cat] || 0) + pen)
    }
    const sorted = Object.entries(catMap)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 5)
    const max = sorted.length > 0 ? sorted[0][1] : 1
    return { items: sorted, max }
  }, [movimientos, currentYear])

  /* ─── Recent Movements ─── */
  const recentMovimientos = useMemo(() => {
    return [...movimientos]
      .sort((a, b) => b.fecha.localeCompare(a.fecha) || b.id - a.id)
      .slice(0, 10)
  }, [movimientos])

  /* ─── Projections & Recurring costs ─── */
  const projections = useMemo(() => {
    const pendientes = programados.filter(p => p.estado === 'pendiente')
    const upcoming = [...pendientes]
      .sort((a, b) => a.fecha_vencimiento.localeCompare(b.fecha_vencimiento))
      .slice(0, 6)
    const totalPendientePEN = r2(pendientes.filter(p => p.moneda === 'PEN').reduce((s, p) => s + p.monto, 0))
    const totalPendienteUSD = r2(pendientes.filter(p => p.moneda === 'USD').reduce((s, p) => s + p.monto, 0))
    const totalConsolidado = r2(totalPendientePEN + totalPendienteUSD * TC_REF)

    // Monthly recurring burn rate
    const activeRecurrentes = recurrentes.filter(r => r.activo)
    const monthlyBurnPEN = r2(activeRecurrentes
      .filter(r => r.moneda === 'PEN')
      .reduce((s, r) => s + r.monto, 0))
    const monthlyBurnUSD = r2(activeRecurrentes
      .filter(r => r.moneda === 'USD')
      .reduce((s, r) => s + r.monto, 0))
    const monthlyBurnTotal = r2(monthlyBurnPEN + monthlyBurnUSD * TC_REF)

    return { upcoming, totalConsolidado, totalPendientePEN, totalPendienteUSD, activeRecurrentes, monthlyBurnTotal, monthlyBurnPEN, monthlyBurnUSD }
  }, [programados, recurrentes])

  /* ─── Financial Health Metrics ─── */
  const healthMetrics = useMemo(() => {
    const yearMovs = movimientos.filter(m => parseDateParts(m.fecha)[0] === currentYear)
    const totalIngPEN = r2(yearMovs.filter(m => m.tipo === 'ingreso').reduce((s, m) => s + (m.moneda === 'USD' ? r2(m.monto * TC_REF) : m.monto), 0))
    const totalEgrPEN = r2(yearMovs.filter(m => m.tipo === 'egreso').reduce((s, m) => s + (m.moneda === 'USD' ? r2(m.monto * TC_REF) : m.monto), 0))
    const saldoAcumulado = r2(totalIngPEN - totalEgrPEN)
    const margen = totalIngPEN > 0 ? r2((saldoAcumulado / totalIngPEN) * 100) : 0
    // Runway: months of cash at current burn rate
    const runway = projections.monthlyBurnTotal > 0 ? Math.floor(saldoAcumulado / projections.monthlyBurnTotal) : Infinity

    return { totalIngPEN, totalEgrPEN, saldoAcumulado, margen, runway }
  }, [movimientos, currentYear, projections.monthlyBurnTotal])

  /* ─── Tooltip handlers ─── */
  const handleMouseEnter = useCallback((data: TooltipData) => {
    setTooltip(data)
  }, [])

  const handleMouseLeave = useCallback(() => {
    setTooltip(null)
  }, [])

  /* ─── Loading state ─── */
  if (loading) return (
    <div className="page-enter">
      <PageHeader breadcrumbs={CRUMBS} />
      <div style={{ color: 'var(--text-secondary)', marginTop: '2rem', textAlign: 'center' }}>
        <div className="auth-spinner" style={{ margin: '0 auto 12px' }} />Cargando...
      </div>
    </div>
  )

  /* ═══════════════════════ STACKED BAR CHART CALCULATIONS ═══════════════════════ */

  const CHART_W = 800
  const CHART_H = 320
  const PAD = { top: 20, right: 20, bottom: 36, left: 56 }
  const plotW = CHART_W - PAD.left - PAD.right
  const plotH = CHART_H - PAD.top - PAD.bottom
  const zeroY = PAD.top + plotH / 2  // center line
  const halfH = plotH / 2

  // Nice Y-axis ticks (symmetric for positive and negative)
  const rawMax = chartData.maxVal
  const niceStep = (() => {
    const rough = rawMax / 3
    const mag = Math.pow(10, Math.floor(Math.log10(rough || 1)))
    const options = [1, 2, 2.5, 5, 10]
    for (const o of options) {
      if (o * mag >= rough) return o * mag
    }
    return mag * 10
  })()
  const yMax = Math.ceil(rawMax / niceStep) * niceStep || niceStep
  const yTicksPos: number[] = []
  for (let v = niceStep; v <= yMax; v += niceStep) yTicksPos.push(v)
  const yTicksNeg: number[] = yTicksPos.map(v => -v)

  // Bar geometry
  const BAR_W = 26
  const barPositions = chartData.months.map((_, i) => PAD.left + (i + 0.5) * (plotW / 12))

  // Saldo neto line path
  const saldoPath = (() => {
    const pts = chartData.months.map((m, i) => {
      const x = barPositions[i]
      const y = zeroY - (m.saldoNeto / yMax) * halfH
      return `${i === 0 ? 'M' : 'L'}${x},${y}`
    })
    return pts.join('')
  })()
  const saldoPathLength = 2000

  // Saldo proyectado (real + cronograma pendiente). Coincide con la linea real
  // en los meses sin nada programado.
  const saldoProyPath = (() => {
    const pts = chartData.months.map((m, i) => {
      const x = barPositions[i]
      const y = zeroY - (m.saldoProyectado / yMax) * halfH
      return `${i === 0 ? 'M' : 'L'}${x},${y}`
    })
    return pts.join('')
  })()
  const hayProyeccion = chartData.months.some(m => m.ingresosProyectados > 0 || m.egresosProyectados > 0)

  // Totales del anio: lo real y lo que queda si se cumple el cronograma pendiente.
  const totalesAnio = (() => {
    const ing = r2(chartData.months.reduce((t, m) => t + m.ingresosTotal, 0))
    const egr = r2(chartData.months.reduce((t, m) => t + m.egresosTotal, 0))
    const ingProy = r2(chartData.months.reduce((t, m) => t + m.ingresosProyectados, 0))
    const egrProy = r2(chartData.months.reduce((t, m) => t + m.egresosProyectados, 0))
    return {
      ing, egr, ingProy, egrProy,
      saldo: r2(ing - egr),
      ingConProy: r2(ing + ingProy),
      egrConProy: r2(egr + egrProy),
      saldoConProy: r2((ing + ingProy) - (egr + egrProy)),
    }
  })()

  /* ═══════════════════════ RENDER ═══════════════════════ */

  return (
    <div className="page-enter" id="dashboard-contabilidad">
      {/* ─── Inline keyframes for chart animation ─── */}
      <style>{`
        @keyframes dashDraw { from { stroke-dashoffset: ${saldoPathLength}; } to { stroke-dashoffset: 0; } }
        @keyframes fadeInUp { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes dotPop { 0% { r: 0; } 60% { r: 5; } 100% { r: 4; } }
      `}</style>

      <PageHeader
        breadcrumbs={CRUMBS}
        meta="Resumen ejecutivo financiero"
        actions={
          <>
            {/* Selector de año */}
            <div style={{ display: 'flex', gap: 2, background: 'var(--bg-primary)', borderRadius: 8, padding: 2, border: '1px solid var(--border)' }}>
              {availableYears.map(y => (
                <button
                  key={y}
                  onClick={() => setSelectedYear(y)}
                  className={`btn btn-sm ${y === selectedYear ? 'btn-primary' : 'btn-ghost'}`}
                  style={{ padding: '4px 12px', fontSize: '0.72rem' }}
                >{y}</button>
              ))}
            </div>
            <Link to="/contabilidad/caja" className="btn btn-primary">
              <IconPlus size={16} /> Nuevo Movimiento
            </Link>
          </>
        }
      />

      {/* ═══════════════════════════════════════════════════════════════════
           KPI CARDS — Mes actual
           ═══════════════════════════════════════════════════════════════════ */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(5, 1fr)',
        gap: 10,
        marginBottom: 10,
      }}>
        {/* Ingresos */}
        <div style={{
          background: 'var(--bg-card)',
          border: '1px solid var(--border)',
          borderRadius: 10,
          padding: '14px 16px',
          position: 'relative',
          overflow: 'hidden',
          animation: 'fadeInUp 0.4s ease both',
        }}>
          <div style={{
            position: 'absolute', top: 0, right: 0, width: 60, height: 60,
            background: 'radial-gradient(circle at top right, rgba(16,185,129,0.10), transparent 70%)',
          }} />
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
            <div style={{
              width: 28, height: 28, borderRadius: 7,
              background: 'color-mix(in srgb, var(--success) 12%, transparent)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth="2">
                <polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/>
              </svg>
            </div>
            <span style={{ fontSize: '0.7rem', color: 'var(--text-secondary)', fontWeight: 500 }}>Ingresos del mes</span>
          </div>
          <div style={{ fontSize: '1.15rem', fontWeight: 700, color: 'var(--success)', letterSpacing: '-0.02em' }}>
            {kpis.usd.ingresos > 0 ? formatCurrency(kpis.usd.ingresos, 'USD') : formatCurrency(kpis.pen.ingresos, 'PEN')}
          </div>
          {kpis.usd.ingresos > 0 && kpis.pen.ingresos > 0 && (
            <div style={{ fontSize: '0.7rem', color: 'var(--text-secondary)', marginTop: 2 }}>
              + {formatCurrency(kpis.pen.ingresos, 'PEN')}
            </div>
          )}
          {kpis.usd.igv_ing > 0 && (
            <div style={{ fontSize: '0.65rem', color: 'var(--text-muted)', marginTop: 4, lineHeight: 1.4 }}>
              Base: {formatCurrency(kpis.usd.base_ing, 'USD')} &middot; IGV: {formatCurrency(kpis.usd.igv_ing, 'USD')}
            </div>
          )}
        </div>

        {/* Egresos */}
        <div style={{
          background: 'var(--bg-card)',
          border: '1px solid var(--border)',
          borderRadius: 10,
          padding: '14px 16px',
          position: 'relative',
          overflow: 'hidden',
          animation: 'fadeInUp 0.4s ease 0.05s both',
        }}>
          <div style={{
            position: 'absolute', top: 0, right: 0, width: 60, height: 60,
            background: 'radial-gradient(circle at top right, rgba(239,68,68,0.10), transparent 70%)',
          }} />
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
            <div style={{
              width: 28, height: 28, borderRadius: 7,
              background: 'color-mix(in srgb, var(--danger) 12%, transparent)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2">
                <polyline points="23 18 13.5 8.5 8.5 13.5 1 6"/>
              </svg>
            </div>
            <span style={{ fontSize: '0.7rem', color: 'var(--text-secondary)', fontWeight: 500 }}>Egresos del mes</span>
          </div>
          <div style={{ fontSize: '1.15rem', fontWeight: 700, color: 'var(--danger)', letterSpacing: '-0.02em' }}>
            {kpis.usd.egresos > 0 ? formatCurrency(kpis.usd.egresos, 'USD') : formatCurrency(kpis.pen.egresos, 'PEN')}
          </div>
          {kpis.usd.egresos > 0 && kpis.pen.egresos > 0 && (
            <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginTop: 4 }}>
              + {formatCurrency(kpis.pen.egresos, 'PEN')}
            </div>
          )}
        </div>

        {/* Saldo Neto */}
        <div style={{
          background: 'var(--bg-card)',
          border: '1px solid var(--border)',
          borderRadius: 10,
          padding: '14px 16px',
          position: 'relative',
          overflow: 'hidden',
          animation: 'fadeInUp 0.4s ease 0.1s both',
        }}>
          <div style={{
            position: 'absolute', top: 0, right: 0, width: 60, height: 60,
            background: 'radial-gradient(circle at top right, rgba(99,102,241,0.10), transparent 70%)',
          }} />
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
            <div style={{
              width: 28, height: 28, borderRadius: 7,
              background: 'color-mix(in srgb, var(--primary-light) 12%, transparent)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#6366f1" strokeWidth="2">
                <line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
              </svg>
            </div>
            <span style={{ fontSize: '0.7rem', color: 'var(--text-secondary)', fontWeight: 500 }}>Saldo neto</span>
          </div>
          {(() => {
            const hasUSD = kpis.usd.ingresos > 0 || kpis.usd.egresos > 0
            const hasPEN = kpis.pen.ingresos > 0 || kpis.pen.egresos > 0
            const saldoColor = (s: number) => s >= 0 ? '#10b981' : '#ef4444'
            return (
              <>
                {hasUSD && (
                  <div style={{ fontSize: '1.15rem', fontWeight: 700, color: saldoColor(kpis.usd.saldo), letterSpacing: '-0.02em' }}>
                    {kpis.usd.saldo >= 0 ? '+' : ''}{formatCurrency(kpis.usd.saldo, 'USD')}
                  </div>
                )}
                {hasPEN && (
                  <div style={{
                    fontSize: hasUSD ? '0.75rem' : '1.15rem',
                    fontWeight: hasUSD ? 500 : 700,
                    color: saldoColor(kpis.pen.saldo),
                    marginTop: hasUSD ? 4 : 0,
                    letterSpacing: '-0.02em',
                  }}>
                    {kpis.pen.saldo >= 0 ? '+' : ''}{formatCurrency(kpis.pen.saldo, 'PEN')}
                  </div>
                )}
                {!hasUSD && !hasPEN && (
                  <div style={{ fontSize: '1.15rem', fontWeight: 700, color: 'var(--text-secondary)', letterSpacing: '-0.02em' }}>+0.00</div>
                )}
              </>
            )
          })()}
        </div>

        {/* Órdenes Pendientes */}
        <Link to="/cobros" style={{ textDecoration: 'none' }}>
          <div style={{
            background: 'var(--bg-card)',
            border: '1px solid var(--border)',
            borderRadius: 10,
            padding: '14px 16px',
            position: 'relative',
            overflow: 'hidden',
            animation: 'fadeInUp 0.4s ease 0.15s both',
            cursor: 'pointer',
            transition: 'border-color 0.2s, box-shadow 0.2s',
            height: '100%',
          }}
          onMouseEnter={e => {
            e.currentTarget.style.borderColor = 'var(--warning)'
            e.currentTarget.style.boxShadow = '0 0 0 1px var(--warning), 0 4px 12px color-mix(in srgb, var(--warning) 15%, transparent)'
          }}
          onMouseLeave={e => {
            e.currentTarget.style.borderColor = 'var(--border)'
            e.currentTarget.style.boxShadow = 'none'
          }}
          >
            <div style={{
              position: 'absolute', top: 0, right: 0, width: 60, height: 60,
              background: 'radial-gradient(circle at top right, color-mix(in srgb, var(--warning) 10%, transparent), transparent 70%)',
            }} />
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
              <div style={{
                width: 24, height: 24, borderRadius: 7,
                background: 'color-mix(in srgb, var(--warning) 12%, transparent)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#f59e0b" strokeWidth="2">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                  <polyline points="14 2 14 8 20 8"/>
                </svg>
              </div>
              <span style={{ fontSize: '0.7rem', color: 'var(--text-secondary)', fontWeight: 500 }}>Órdenes pendientes</span>
            </div>
            <div style={{ fontSize: '1.15rem', fontWeight: 700, color: 'var(--warning)', letterSpacing: '-0.02em' }}>
              {kpis.facturasPendientes}
            </div>
            <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginTop: 4 }}>
              {formatCurrency(kpis.totalPendientePEN, 'PEN')} por cobrar
              {kpis.totalPendienteUSD > 0 && (
                <span style={{ marginLeft: 6 }}>+ {formatCurrency(kpis.totalPendienteUSD, 'USD')}</span>
              )}
            </div>
          </div>
        </Link>

        {/* Ingreso Promedio */}
        <div style={{
          background: 'var(--bg-card)',
          border: '1px solid var(--border)',
          borderRadius: 10,
          padding: '14px 16px',
          position: 'relative',
          overflow: 'hidden',
          animation: 'fadeInUp 0.4s ease 0.2s both',
        }}>
          <div style={{
            position: 'absolute', top: 0, right: 0, width: 60, height: 60,
            background: 'radial-gradient(circle at top right, rgba(139,92,246,0.10), transparent 70%)',
          }} />
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
            <div style={{
              width: 24, height: 24, borderRadius: 7,
              background: 'color-mix(in srgb, var(--primary-light) 12%, transparent)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#8b5cf6" strokeWidth="2">
                <path d="M12 20V10"/><path d="M18 20V4"/><path d="M6 20v-4"/>
              </svg>
            </div>
            <span style={{ fontSize: '0.7rem', color: 'var(--text-secondary)', fontWeight: 500 }}>Ingreso prom/mes</span>
          </div>
          <div style={{ fontSize: '1.15rem', fontWeight: 700, color: 'var(--primary-light)', letterSpacing: '-0.02em' }}>
            {formatCurrency(kpis.ingresoPromedio, 'PEN')}
          </div>
          <div style={{ fontSize: '0.72rem', color: 'var(--text-secondary)', marginTop: 4 }}>
            {formatCurrency(kpis.totalIngConsolidado, 'PEN')} / {kpis.mesesTranscurridos} meses
          </div>
        </div>
      </div>

      {/* ═══════════════════════════════════════════════════════════════════
           RESUMEN TRIBUTARIO — Año actual
           ═══════════════════════════════════════════════════════════════════ */}
      {(tributario.usd.igv_cobrado > 0 || tributario.pen.igv_cobrado > 0) && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
          gap: 10,
          marginBottom: 14,
        }}>
          {[
            { label: `IGV Cobrado (${currentYear})`, usd: tributario.usd.igv_cobrado, pen: tributario.pen.igv_cobrado, color: 'var(--success)', border: 'var(--success)' },
            { label: `IGV Pagado (${currentYear})`, usd: tributario.usd.igv_pagado, pen: tributario.pen.igv_pagado, color: 'var(--danger)', border: 'var(--danger)' },
            { label: 'IGV Neto a Declarar', usd: tributario.usd.igv_neto, pen: tributario.pen.igv_neto, color: 'var(--primary-light)', border: 'var(--primary-light)' },
            { label: 'Detracciones SPOT', usd: tributario.usd.detracciones, pen: tributario.pen.detracciones, color: 'var(--warning)', border: 'var(--warning)' },
          ].map((item, idx) => (
            <div key={idx} style={{
              background: 'var(--bg-card)',
              border: '1px solid var(--border)',
              borderLeft: `3px solid ${item.border}`,
              borderRadius: 8,
              padding: '10px 14px',
              animation: `fadeInUp 0.4s ease ${0.2 + idx * 0.05}s both`,
            }}>
              <div style={{ fontSize: '0.65rem', color: 'var(--text-secondary)', marginBottom: 4, fontWeight: 500 }}>
                {item.label}
              </div>
              {item.usd > 0 && (
                <div style={{ fontSize: '0.9rem', fontWeight: 700, color: item.color }}>
                  {formatCurrency(item.usd, 'USD')}
                </div>
              )}
              {item.pen > 0 && (
                <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginTop: 2 }}>
                  {formatCurrency(item.pen, 'PEN')}
                </div>
              )}
              {item.usd === 0 && item.pen === 0 && (
                <div style={{ fontSize: '0.9rem', fontWeight: 700, color: 'var(--text-muted)' }}>—</div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════════════
           12-MONTH STACKED BAR CHART — Flujo de Caja (Consolidado PEN)
           ═══════════════════════════════════════════════════════════════════ */}
      <div style={{
        background: 'var(--bg-card)',
        border: '1px solid var(--border)',
        borderRadius: 12,
        padding: '16px 20px',
        marginBottom: 14,
        position: 'relative',
        animation: 'fadeInUp 0.5s ease 0.25s both',
      }}>
        {/* Chart Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <div>
            <h2 style={{ fontSize: '1.1rem', fontWeight: 700, color: 'var(--text-primary)', margin: 0 }}>
              Flujo de Caja {currentYear}
            </h2>
            <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', margin: '4px 0 0' }}>
              Ingresos vs egresos consolidado en PEN (TC ref: {TC_REF})
            </p>
            {/* Totales del anio: real, y el mismo total sumando el cronograma pendiente */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap', marginTop: 8, fontSize: '0.72rem' }}>
              <span style={{ color: 'var(--text-muted)' }}>
                Real:{' '}
                <strong style={{ color: 'var(--success)' }}>{formatCurrency(totalesAnio.ing, 'PEN')}</strong>
                {' / '}
                <strong style={{ color: 'var(--danger)' }}>{formatCurrency(totalesAnio.egr, 'PEN')}</strong>
                {' · saldo '}
                <strong style={{ color: totalesAnio.saldo >= 0 ? 'var(--success)' : 'var(--danger)' }}>
                  {formatCurrency(totalesAnio.saldo, 'PEN')}
                </strong>
              </span>
              {hayProyeccion && (
                <span style={{ color: 'var(--text-muted)' }}>
                  C/ proyectado:{' '}
                  <strong style={{ color: 'var(--success)' }}>{formatCurrency(totalesAnio.ingConProy, 'PEN')}</strong>
                  {' / '}
                  <strong style={{ color: 'var(--danger)' }}>{formatCurrency(totalesAnio.egrConProy, 'PEN')}</strong>
                  {' · saldo '}
                  <strong style={{ color: totalesAnio.saldoConProy >= 0 ? 'var(--warning)' : 'var(--danger)' }}>
                    {formatCurrency(totalesAnio.saldoConProy, 'PEN')}
                  </strong>
                </span>
              )}
            </div>
          </div>
          {/* Legend */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, fontSize: '0.65rem', color: 'var(--text-muted)', flexWrap: 'wrap' }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
              <span style={{ width: 7, height: 7, borderRadius: 1, background: 'var(--success)', display: 'inline-block' }} />
              USD→PEN
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
              <span style={{ width: 7, height: 7, borderRadius: 1, background: 'color-mix(in srgb, var(--success) 55%, transparent)', display: 'inline-block' }} />
              PEN
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
              <span style={{ width: 7, height: 7, borderRadius: 1, background: 'var(--danger)', display: 'inline-block' }} />
              Egresos USD
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
              <span style={{ width: 7, height: 7, borderRadius: 1, background: 'color-mix(in srgb, var(--danger) 45%, transparent)', display: 'inline-block' }} />
              Egresos PEN
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
              <svg width="16" height="2"><line x1="0" y1="1" x2="16" y2="1" stroke="rgba(167,139,250,0.6)" strokeWidth="1.5" strokeDasharray="3 2" /></svg>
              Saldo
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
              <span style={{ width: 7, height: 7, borderRadius: 1, background: 'repeating-linear-gradient(45deg, rgba(251,146,60,0.5), rgba(251,146,60,0.5) 1px, transparent 1px, transparent 3px)', display: 'inline-block' }} />
              Egr. Proyectado
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
              <span style={{ width: 7, height: 7, borderRadius: 1, background: 'repeating-linear-gradient(45deg, rgba(16,185,129,0.45), rgba(16,185,129,0.45) 1px, transparent 1px, transparent 3px)', display: 'inline-block' }} />
              Ing. Proyectado
            </span>
            {hayProyeccion && (
              <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                <svg width="16" height="2"><line x1="0" y1="1" x2="16" y2="1" stroke="rgba(251,191,36,0.75)" strokeWidth="1.5" strokeDasharray="2 2" /></svg>
                Saldo proyectado
              </span>
            )}
          </div>
        </div>

        {/* SVG Stacked Bar Chart */}
        <div style={{ position: 'relative', minHeight: 340 }}>
          <svg
            viewBox={`0 0 ${CHART_W} ${CHART_H}`}
            style={{ width: '100%', height: 'auto', display: 'block' }}
            preserveAspectRatio="xMidYMid meet"
          >
            {/* Projection stripe patterns */}
            <defs>
              <pattern id="proj-stripes" width="4" height="4" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
                <line x1="0" y1="0" x2="0" y2="4" stroke="rgba(251,146,60,0.5)" strokeWidth="1" />
              </pattern>
              <pattern id="proj-stripes-ing" width="4" height="4" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
                <line x1="0" y1="0" x2="0" y2="4" stroke="rgba(16,185,129,0.45)" strokeWidth="1" />
              </pattern>
            </defs>

            {/* Positive grid lines */}
            {yTicksPos.map((v, i) => {
              const y = zeroY - (v / yMax) * halfH
              return (
                <g key={`pos-${i}`}>
                  <line
                    x1={PAD.left} y1={y} x2={CHART_W - PAD.right} y2={y}
                    stroke="var(--border)" strokeDasharray="4 4" opacity={0.25}
                  />
                  <text
                    x={PAD.left - 8} y={y + 4}
                    textAnchor="end" fontSize="9" fill="var(--text-muted)"
                    fontFamily="inherit"
                  >
                    {formatShort(v, 'PEN')}
                  </text>
                </g>
              )
            })}

            {/* Negative grid lines */}
            {yTicksNeg.map((v, i) => {
              const y = zeroY - (v / yMax) * halfH
              return (
                <g key={`neg-${i}`}>
                  <line
                    x1={PAD.left} y1={y} x2={CHART_W - PAD.right} y2={y}
                    stroke="var(--border)" strokeDasharray="4 4" opacity={0.25}
                  />
                  <text
                    x={PAD.left - 8} y={y + 4}
                    textAnchor="end" fontSize="9" fill="var(--text-muted)"
                    fontFamily="inherit"
                  >
                    {formatShort(v, 'PEN')}
                  </text>
                </g>
              )
            })}

            {/* Zero line */}
            <line
              x1={PAD.left} y1={zeroY} x2={CHART_W - PAD.right} y2={zeroY}
              stroke="var(--text-muted)" strokeWidth={1} opacity={0.5}
            />
            <text
              x={PAD.left - 8} y={zeroY + 4}
              textAnchor="end" fontSize="9" fill="var(--text-muted)"
              fontFamily="inherit"
            >
              S/0
            </text>

            {/* Stacked Bars */}
            {chartData.months.map((m, i) => {
              const cx = barPositions[i]
              const barX = cx - BAR_W / 2

              // INGRESOS (above zero line) — PEN native bottom, USD→PEN top
              const ingPenH = yMax > 0 ? (m.ingresosPEN / yMax) * halfH : 0
              const ingUsdH = yMax > 0 ? (m.ingresosUSDpen / yMax) * halfH : 0

              // EGRESOS (below zero line) — PEN native top (closer to zero), USD→PEN bottom
              const egrPenH = yMax > 0 ? (m.egresosPEN / yMax) * halfH : 0
              const egrUsdH = yMax > 0 ? (m.egresosUSDpen / yMax) * halfH : 0

              return (
                <g key={`bar-${i}`}>
                  {/* Ingresos: PEN native (bottom segment) */}
                  {ingPenH > 0 && (
                    <rect
                      x={barX} y={zeroY - ingPenH}
                      width={BAR_W} height={ingPenH}
                      fill="rgba(16,185,129,0.55)"
                      style={{ transition: 'height 0.6s cubic-bezier(0.4,0,0.2,1), y 0.6s cubic-bezier(0.4,0,0.2,1)' }}
                    />
                  )}
                  {/* Ingresos: USD→PEN (top segment) */}
                  {ingUsdH > 0 && (
                    <rect
                      x={barX} y={zeroY - ingPenH - ingUsdH}
                      width={BAR_W} height={ingUsdH}
                      fill="#065f46"
                      style={{ transition: 'height 0.6s cubic-bezier(0.4,0,0.2,1), y 0.6s cubic-bezier(0.4,0,0.2,1)' }}
                    />
                  )}

                  {/* Egresos: PEN native (closer to zero) */}
                  {egrPenH > 0 && (
                    <rect
                      x={barX} y={zeroY}
                      width={BAR_W} height={egrPenH}
                      fill="rgba(239,68,68,0.45)"
                      style={{ transition: 'height 0.6s cubic-bezier(0.4,0,0.2,1)' }}
                    />
                  )}
                  {/* Egresos: USD→PEN (bottom segment) */}
                  {egrUsdH > 0 && (
                    <rect
                      x={barX} y={zeroY + egrPenH}
                      width={BAR_W} height={egrUsdH}
                      fill="#991b1b"
                      style={{ transition: 'height 0.6s cubic-bezier(0.4,0,0.2,1)' }}
                    />
                  )}
                  {/* Projected ingresos (green striped, above actual) */}
                  {m.ingresosProyectados > 0 && (
                    <rect
                      x={barX} y={zeroY - ingPenH - ingUsdH - (yMax > 0 ? (m.ingresosProyectados / yMax) * halfH : 0)}
                      width={BAR_W}
                      height={yMax > 0 ? (m.ingresosProyectados / yMax) * halfH : 0}
                      fill="url(#proj-stripes-ing)"
                      stroke="rgba(16,185,129,0.3)"
                      strokeWidth="0.5"
                      style={{ transition: 'height 0.6s cubic-bezier(0.4,0,0.2,1)' }}
                    />
                  )}
                  {/* Projected egresos (striped) */}
                  {m.egresosProyectados > 0 && (
                    <rect
                      x={barX} y={zeroY + egrPenH + egrUsdH}
                      width={BAR_W}
                      height={yMax > 0 ? (m.egresosProyectados / yMax) * halfH : 0}
                      fill="url(#proj-stripes)"
                      stroke="rgba(251,146,60,0.3)"
                      strokeWidth="0.5"
                      style={{ transition: 'height 0.6s cubic-bezier(0.4,0,0.2,1)' }}
                    />
                  )}
                </g>
              )
            })}

            {/* Saldo proyectado line (real + cronograma pendiente) */}
            {hayProyeccion && (
              <path
                d={saldoProyPath}
                fill="none" stroke="rgba(251,191,36,0.75)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"
                strokeDasharray="3 3"
                style={{ animation: 'fadeInUp 0.6s ease 0.5s both' }}
              />
            )}

            {/* Saldo neto line */}
            <path
              d={saldoPath}
              fill="none" stroke="rgba(167,139,250,0.6)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"
              strokeDasharray="5 3"
              style={{
                strokeDasharray: saldoPathLength,
                strokeDashoffset: 0,
                animation: 'dashDraw 1.5s ease-out 0.3s both',
              }}
            />

            {/* Saldo neto dots */}
            {chartData.months.map((m, i) => {
              const cy = zeroY - (m.saldoNeto / yMax) * halfH
              return (
                <circle
                  key={`sn-${i}`}
                  cx={barPositions[i]} cy={cy} r={2.5}
                  fill="rgba(167,139,250,0.7)" stroke="var(--bg-card)" strokeWidth="1"
                  style={{ animation: `dotPop 0.3s ease ${0.8 + i * 0.04}s both` }}
                />
              )
            })}

            {/* Saldo proyectado dots (solo donde hay cronograma) */}
            {hayProyeccion && chartData.months.map((m, i) => {
              if (m.saldoProyectado === m.saldoNeto) return null
              const cy = zeroY - (m.saldoProyectado / yMax) * halfH
              return (
                <circle
                  key={`sp-${i}`}
                  cx={barPositions[i]} cy={cy} r={2.5}
                  fill="rgba(251,191,36,0.85)" stroke="var(--bg-card)" strokeWidth="1"
                  style={{ animation: `dotPop 0.3s ease ${0.9 + i * 0.04}s both` }}
                />
              )
            })}

            {/* X-axis month labels */}
            {chartData.months.map((m, i) => (
              <text
                key={i}
                x={barPositions[i]}
                y={CHART_H - 10}
                textAnchor="middle"
                fontSize="10"
                fill="var(--text-muted)"
                fontFamily="inherit"
                fontWeight={i === now.getMonth() && currentYear === new Date().getFullYear() ? 700 : 400}
              >
                {m.label}
              </text>
            ))}

            {/* Invisible hover zones for tooltip */}
            {chartData.months.map((m, i) => {
              const cx = barPositions[i]
              const colW = plotW / 12
              return (
                <rect
                  key={`hz-${i}`}
                  x={cx - colW / 2}
                  y={PAD.top}
                  width={colW}
                  height={plotH}
                  fill="transparent"
                  style={{ cursor: 'pointer' }}
                  onMouseEnter={() => handleMouseEnter({
                    label: m.label,
                    ingresosUSD: m.ingresosUSD,
                    egresosUSD: m.egresosUSD,
                    ingresosPEN: m.ingresosPEN,
                    egresosPEN: m.egresosPEN,
                    ingresosTotal: m.ingresosTotal,
                    egresosTotal: m.egresosTotal,
                    saldoNeto: m.saldoNeto,
                    ingresosProyectados: m.ingresosProyectados,
                    egresosProyectados: m.egresosProyectados,
                    saldoProyectado: m.saldoProyectado,
                    x: cx,
                    y: zeroY - ((m.ingresosTotal + m.ingresosProyectados) / yMax) * halfH - 10,
                  })}
                  onMouseLeave={handleMouseLeave}
                />
              )
            })}
          </svg>

          {/* Tooltip (HTML overlay) */}
          {tooltip && (
            <div style={{
              position: 'absolute',
              left: `${(tooltip.x / CHART_W) * 100}%`,
              top: `${(tooltip.y / CHART_H) * 100}%`,
              transform: 'translate(-50%, -100%)',
              // Vidrio oscuro en los DOS temas, a proposito: el tooltip
              // flota sobre el grafico y tiene que despegarse de el, que ya
              // es una superficie clara. Por eso el blanco de adentro es
              // correcto y no se toca.
              background: 'rgba(15,15,25,0.94)',
              backdropFilter: 'blur(12px)',
              border: '1px solid rgba(255,255,255,0.1)',
              borderRadius: 10,
              padding: '10px 14px',
              pointerEvents: 'none',
              zIndex: 10,
              minWidth: 200,
              boxShadow: '0 8px 32px rgba(var(--shadow-rgb), calc(.4 * var(--shadow-k)))',
            }}>
              <div style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--text-on-primary)', marginBottom: 6 }}>
                {tooltip.label} {currentYear}
              </div>
              <div style={{ borderTop: '1px solid rgba(255,255,255,0.15)', margin: '4px 0' }} />
              {/* Ingresos */}
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.72rem', marginBottom: 3 }}>
                <span style={{ color: 'var(--success)', fontWeight: 600 }}>Ingresos</span>
                <span style={{ color: 'var(--success)', fontWeight: 700 }}>{formatCurrency(tooltip.ingresosTotal, 'PEN')}</span>
              </div>
              {tooltip.ingresosUSD > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.65rem', marginBottom: 1, paddingLeft: 8 }}>
                  <span style={{ color: 'rgba(255,255,255,0.5)' }}>USD → PEN</span>
                  <span style={{ color: 'rgba(255,255,255,0.7)' }}>{formatCurrency(r2(tooltip.ingresosUSD * TC_REF), 'PEN')} <span style={{ color: 'rgba(255,255,255,0.4)' }}>({formatCurrency(tooltip.ingresosUSD, 'USD')})</span></span>
                </div>
              )}
              {tooltip.ingresosPEN > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.65rem', marginBottom: 1, paddingLeft: 8 }}>
                  <span style={{ color: 'rgba(255,255,255,0.5)' }}>PEN nativo</span>
                  <span style={{ color: 'rgba(255,255,255,0.7)' }}>{formatCurrency(tooltip.ingresosPEN, 'PEN')}</span>
                </div>
              )}
              {tooltip.ingresosProyectados > 0 && (
                <>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.65rem', marginBottom: 1, paddingLeft: 8 }}>
                    <span style={{ color: 'rgba(255,255,255,0.5)' }}>Proyectado</span>
                    <span style={{ color: 'color-mix(in srgb, var(--success) 75%, transparent)' }}>+{formatCurrency(tooltip.ingresosProyectados, 'PEN')}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.68rem', marginTop: 2, paddingLeft: 8 }}>
                    <span style={{ color: 'rgba(255,255,255,0.65)', fontWeight: 600 }}>Total c/ proyectado</span>
                    <span style={{ color: 'var(--success)', fontWeight: 700 }}>{formatCurrency(r2(tooltip.ingresosTotal + tooltip.ingresosProyectados), 'PEN')}</span>
                  </div>
                </>
              )}
              <div style={{ borderTop: '1px solid rgba(255,255,255,0.1)', margin: '4px 0' }} />
              {/* Egresos */}
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.72rem', marginBottom: 3 }}>
                <span style={{ color: 'var(--danger)', fontWeight: 600 }}>Egresos</span>
                <span style={{ color: 'var(--danger)', fontWeight: 700 }}>{formatCurrency(tooltip.egresosTotal, 'PEN')}</span>
              </div>
              {tooltip.egresosUSD > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.65rem', marginBottom: 1, paddingLeft: 8 }}>
                  <span style={{ color: 'rgba(255,255,255,0.5)' }}>USD → PEN</span>
                  <span style={{ color: 'rgba(255,255,255,0.7)' }}>{formatCurrency(r2(tooltip.egresosUSD * TC_REF), 'PEN')} <span style={{ color: 'rgba(255,255,255,0.4)' }}>({formatCurrency(tooltip.egresosUSD, 'USD')})</span></span>
                </div>
              )}
              {tooltip.egresosPEN > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.65rem', marginBottom: 1, paddingLeft: 8 }}>
                  <span style={{ color: 'rgba(255,255,255,0.5)' }}>PEN nativo</span>
                  <span style={{ color: 'rgba(255,255,255,0.7)' }}>{formatCurrency(tooltip.egresosPEN, 'PEN')}</span>
                </div>
              )}
              {tooltip.egresosProyectados > 0 && (
                <>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.65rem', marginBottom: 1, paddingLeft: 8 }}>
                    <span style={{ color: 'rgba(255,255,255,0.5)' }}>Proyectado</span>
                    <span style={{ color: 'rgba(251,146,60,0.85)' }}>+{formatCurrency(tooltip.egresosProyectados, 'PEN')}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.68rem', marginTop: 2, paddingLeft: 8 }}>
                    <span style={{ color: 'rgba(255,255,255,0.65)', fontWeight: 600 }}>Total c/ proyectado</span>
                    <span style={{ color: 'var(--danger)', fontWeight: 700 }}>{formatCurrency(r2(tooltip.egresosTotal + tooltip.egresosProyectados), 'PEN')}</span>
                  </div>
                </>
              )}
              <div style={{ borderTop: '1px solid rgba(255,255,255,0.1)', margin: '4px 0' }} />
              {/* Saldo Neto */}
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.72rem' }}>
                <span style={{ color: 'var(--primary-light)', fontWeight: 600 }}>Saldo Neto</span>
                <span style={{ color: tooltip.saldoNeto >= 0 ? 'var(--success)' : 'var(--danger)', fontWeight: 700 }}>
                  S/{tooltip.saldoNeto >= 0 ? '+' : ''}{tooltip.saldoNeto.toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
              </div>
              {(tooltip.ingresosProyectados > 0 || tooltip.egresosProyectados > 0) && (
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.72rem', marginTop: 3 }}>
                  <span style={{ color: 'var(--warning)', fontWeight: 600 }}>Saldo proyectado</span>
                  <span style={{ color: tooltip.saldoProyectado >= 0 ? 'var(--success)' : 'var(--danger)', fontWeight: 700 }}>
                    S/{tooltip.saldoProyectado >= 0 ? '+' : ''}{tooltip.saldoProyectado.toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </span>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* ═══════════════════════════════════════════════════════════════════
           BOTTOM ROW — Category Breakdown + Upcoming Payments + Health Metrics
           ═══════════════════════════════════════════════════════════════════ */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr 1fr',
        gap: 12,
        marginBottom: 12,
      }}>

        {/* ── COL 1: Distribución por Categoría ── */}
        <div style={{
          background: 'var(--bg-card)',
          border: '1px solid var(--border)',
          borderRadius: 10,
          padding: '16px 18px',
          animation: 'fadeInUp 0.5s ease 0.3s both',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
            <h2 style={{ fontSize: '0.85rem', fontWeight: 700, color: 'var(--text-primary)', margin: 0 }}>
              Distribución por Categoría
            </h2>
            <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)', fontWeight: 500 }}>
              Egresos {currentYear} (S/)
            </span>
          </div>

          {categoryData.items.length === 0 ? (
            <div style={{ color: 'var(--text-secondary)', textAlign: 'center', padding: '2rem 0', fontSize: '0.85rem' }}>
              Sin egresos registrados
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {categoryData.items.map(([cat, amount], idx) => {
                const color = CATEGORY_COLORS[cat] || CATEGORY_FALLBACK
                const pct = categoryData.max > 0 ? (amount / categoryData.max) * 100 : 0
                return (
                  <div key={cat} style={{ animation: `fadeInUp 0.3s ease ${0.35 + idx * 0.05}s both` }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{
                          width: 10, height: 10, borderRadius: 3,
                          background: color, display: 'inline-block',
                        }} />
                        <span style={{ fontSize: '0.75rem', color: 'var(--text-primary)', fontWeight: 500 }}>
                          {cat}
                        </span>
                      </div>
                      <span style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--text-primary)' }}>
                        {formatCurrency(amount, 'PEN')}
                      </span>
                    </div>
                    <div style={{
                      height: 6,
                      background: 'var(--bg-primary)',
                      borderRadius: 4,
                      overflow: 'hidden',
                    }}>
                      <div style={{
                        height: '100%',
                        width: `${pct}%`,
                        background: `linear-gradient(90deg, ${color}, ${color}cc)`,
                        borderRadius: 4,
                        transition: 'width 0.8s cubic-bezier(0.4,0,0.2,1)',
                      }} />
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>

        {/* ── COL 2: Próximos Vencimientos ── */}
        <div style={{
          background: 'var(--bg-card)',
          border: '1px solid var(--border)',
          borderRadius: 10,
          padding: '16px 18px',
          animation: 'fadeInUp 0.5s ease 0.35s both',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <h2 style={{ fontSize: '0.85rem', fontWeight: 700, color: 'var(--text-primary)', margin: 0 }}>
              Próximos Vencimientos
            </h2>
            <Link
              to="/contabilidad/pagos-programados"
              style={{
                fontSize: '0.72rem', fontWeight: 600,
                color: 'var(--primary)',
                textDecoration: 'none',
                padding: '4px 10px',
                borderRadius: 6,
                border: '1px solid var(--border)',
                transition: 'all 0.2s',
              }}
              onMouseEnter={e => {
                e.currentTarget.style.background = 'var(--primary)'
                e.currentTarget.style.color = '#fff'
                e.currentTarget.style.borderColor = 'var(--primary)'
              }}
              onMouseLeave={e => {
                e.currentTarget.style.background = 'transparent'
                e.currentTarget.style.color = 'var(--primary)'
                e.currentTarget.style.borderColor = 'var(--border)'
              }}
            >
              Ver todos <IconChevronRight size={12} />
            </Link>
          </div>

          {projections.upcoming.length === 0 ? (
            <div style={{ color: 'var(--text-secondary)', textAlign: 'center', padding: '2rem 0', fontSize: '0.85rem' }}>
              Sin pagos pendientes
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
              {projections.upcoming.map((p, idx) => (
                <div
                  key={p.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    padding: '8px 0',
                    borderBottom: idx < projections.upcoming.length - 1 ? '1px solid var(--border)' : 'none',
                    animation: `fadeInUp 0.3s ease ${0.4 + idx * 0.04}s both`,
                  }}
                >
                  <div style={{
                    width: 3, height: 32, borderRadius: 2, flexShrink: 0,
                    background: p.tipo === 'egreso' ? 'var(--warning)' : 'var(--success)',
                  }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{
                      fontSize: '0.75rem', fontWeight: 500, color: 'var(--text-primary)',
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}>
                      {p.descripcion}{p.pago_recurrente_id && <IconRefresh size={11} style={{ marginLeft: 5, verticalAlign: '-1px', color: 'var(--text-muted)' }} />}
                    </div>
                    <div style={{ fontSize: '0.68rem', color: 'var(--text-muted)' }}>
                      {formatDate(p.fecha_vencimiento)}
                    </div>
                  </div>
                  <div style={{
                    fontSize: '0.78rem', fontWeight: 700, whiteSpace: 'nowrap',
                    color: p.tipo === 'egreso' ? 'var(--warning)' : 'var(--success)',
                  }}>
                    {p.tipo === 'egreso' ? '-' : '+'}{formatCurrency(p.monto, p.moneda)}
                  </div>
                </div>
              ))}
              {/* Total */}
              <div style={{
                marginTop: 10, paddingTop: 10,
                borderTop: '1px solid var(--border)',
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              }}>
                <span style={{ fontSize: '0.72rem', color: 'var(--text-secondary)', fontWeight: 500 }}>Total pendiente</span>
                <span style={{ fontSize: '0.85rem', fontWeight: 700, color: 'var(--warning)' }}>
                  {formatCurrency(projections.totalConsolidado, 'PEN')}
                </span>
              </div>
            </div>
          )}
        </div>

        {/* ── COL 3: Salud Financiera ── */}
        <div style={{
          background: 'var(--bg-card)',
          border: '1px solid var(--border)',
          borderRadius: 10,
          padding: '16px 18px',
          animation: 'fadeInUp 0.5s ease 0.4s both',
        }}>
          <h2 style={{ fontSize: '0.85rem', fontWeight: 700, color: 'var(--text-primary)', margin: '0 0 14px' }}>
            Salud Financiera
          </h2>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {/* Saldo Acumulado YTD */}
            <div style={{
              padding: '10px 12px', borderRadius: 8,
              background: healthMetrics.saldoAcumulado >= 0
                ? 'rgba(16,185,129,0.06)'
                : 'rgba(239,68,68,0.06)',
              border: `1px solid ${healthMetrics.saldoAcumulado >= 0 ? 'color-mix(in srgb, var(--success) 15%, transparent)' : 'rgba(239,68,68,0.15)'}`,
            }}>
              <div style={{ fontSize: '0.68rem', color: 'var(--text-muted)', marginBottom: 4, fontWeight: 500 }}>Saldo Acumulado YTD</div>
              <div style={{
                fontSize: '1.3rem', fontWeight: 800, letterSpacing: '-0.03em',
                color: healthMetrics.saldoAcumulado >= 0 ? 'var(--success)' : 'var(--danger)',
              }}>
                {healthMetrics.saldoAcumulado >= 0 ? '+' : ''}{formatCurrency(healthMetrics.saldoAcumulado, 'PEN')}
              </div>
            </div>

            {/* Margen Operativo */}
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                <span style={{ fontSize: '0.72rem', color: 'var(--text-secondary)', fontWeight: 500 }}>Margen Operativo</span>
                <span style={{
                  fontSize: '0.85rem', fontWeight: 700,
                  color: healthMetrics.margen > 30 ? 'var(--success)' : healthMetrics.margen > 10 ? 'var(--warning)' : 'var(--danger)',
                }}>
                  {healthMetrics.margen}%
                </span>
              </div>
              <div style={{ height: 4, background: 'var(--bg-primary)', borderRadius: 2, overflow: 'hidden' }}>
                <div style={{
                  height: '100%', borderRadius: 2,
                  width: `${Math.min(Math.max(healthMetrics.margen, 0), 100)}%`,
                  background: healthMetrics.margen > 30 ? 'var(--success)' : healthMetrics.margen > 10 ? 'var(--warning)' : 'var(--danger)',
                  transition: 'width 0.8s cubic-bezier(0.4,0,0.2,1)',
                }} />
              </div>
            </div>

            {/* Burn Rate Mensual */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ fontSize: '0.72rem', color: 'var(--text-secondary)', fontWeight: 500 }}>Burn Rate Mensual</div>
                <div style={{ fontSize: '0.62rem', color: 'var(--text-muted)', marginTop: 2 }}>
                  {projections.monthlyBurnPEN > 0 && `S/${projections.monthlyBurnPEN.toLocaleString('es-PE', { minimumFractionDigits: 2 })}`}
                  {projections.monthlyBurnPEN > 0 && projections.monthlyBurnUSD > 0 && ' + '}
                  {projections.monthlyBurnUSD > 0 && `$${projections.monthlyBurnUSD.toLocaleString('es-PE', { minimumFractionDigits: 2 })}`}
                </div>
              </div>
              <span style={{ fontSize: '0.85rem', fontWeight: 700, color: 'var(--danger)' }}>
                {formatCurrency(projections.monthlyBurnTotal, 'PEN')}
              </span>
            </div>

            {/* Runway */}
            <div style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              padding: '8px 12px', borderRadius: 8,
              background: 'var(--bg-primary)',
              border: '1px solid var(--border)',
            }}>
              <span style={{ fontSize: '0.72rem', color: 'var(--text-secondary)', fontWeight: 500 }}>Runway</span>
              <span style={{
                fontSize: '0.9rem', fontWeight: 800,
                color: healthMetrics.runway === Infinity ? 'var(--success)'
                  : healthMetrics.runway > 6 ? '#10b981'
                  : healthMetrics.runway > 3 ? '#f59e0b'
                  : '#ef4444',
              }}>
                {healthMetrics.runway === Infinity ? '∞' : `${healthMetrics.runway} meses`}
              </span>
            </div>

            {/* Costos Recurrentes */}
            {projections.activeRecurrentes.length > 0 && (
              <div>
                <div style={{ fontSize: '0.68rem', color: 'var(--text-muted)', fontWeight: 600, marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  Costos Recurrentes ({projections.activeRecurrentes.length})
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  {projections.activeRecurrentes.slice(0, 5).map(r => (
                    <div key={r.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{
                        fontSize: '0.7rem', color: 'var(--text-secondary)',
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 140,
                      }}>
                        {r.descripcion}
                      </span>
                      <span style={{ fontSize: '0.7rem', fontWeight: 600, color: 'var(--text-primary)', whiteSpace: 'nowrap' }}>
                        {formatCurrency(r.monto, r.moneda)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ═══════════════════════════════════════════════════════════════════
           FULL-WIDTH ROW — Últimos Movimientos
           ═══════════════════════════════════════════════════════════════════ */}
      <div style={{
        background: 'var(--bg-card)',
        border: '1px solid var(--border)',
        borderRadius: 10,
        padding: '16px 18px',
        animation: 'fadeInUp 0.5s ease 0.45s both',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <h2 style={{ fontSize: '0.85rem', fontWeight: 700, color: 'var(--text-primary)', margin: 0 }}>
            Últimos Movimientos
          </h2>
          <Link
            to="/contabilidad/caja"
            style={{
              fontSize: '0.72rem', fontWeight: 600,
              color: 'var(--primary)',
              textDecoration: 'none',
              padding: '4px 10px',
              borderRadius: 6,
              border: '1px solid var(--border)',
              transition: 'all 0.2s',
            }}
            onMouseEnter={e => {
              e.currentTarget.style.background = 'var(--primary)'
              e.currentTarget.style.color = '#fff'
              e.currentTarget.style.borderColor = 'var(--primary)'
            }}
            onMouseLeave={e => {
              e.currentTarget.style.background = 'transparent'
              e.currentTarget.style.color = 'var(--primary)'
              e.currentTarget.style.borderColor = 'var(--border)'
            }}
          >
            Ver todos <IconChevronRight size={12} />
          </Link>
        </div>

        {recentMovimientos.length === 0 ? (
          <div style={{ color: 'var(--text-secondary)', textAlign: 'center', padding: '2rem 0', fontSize: '0.85rem' }}>
            Sin movimientos registrados
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '0 24px' }}>
            {recentMovimientos.slice(0, 10).map((m, idx) => (
              <div
                key={m.id}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '7px 0',
                  borderBottom: '1px solid var(--border)',
                  animation: `fadeInUp 0.3s ease ${0.5 + idx * 0.03}s both`,
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0, flex: 1 }}>
                  <div style={{
                    width: 24, height: 24, borderRadius: 6, flexShrink: 0,
                    background: m.tipo === 'ingreso' ? 'color-mix(in srgb, var(--success) 10%, transparent)' : 'rgba(239,68,68,0.1)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '0.65rem', fontWeight: 700,
                    color: m.tipo === 'ingreso' ? 'var(--success)' : 'var(--danger)',
                  }}>
                    {m.tipo === 'ingreso' ? <IconArrowUp size={13} /> : <IconArrowDown size={13} />}
                  </div>
                  <div style={{ minWidth: 0 }}>
                    <div style={{
                      fontSize: '0.75rem', fontWeight: 500, color: 'var(--text-primary)',
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      maxWidth: 260,
                    }}>
                      {m.descripcion || '—'}
                    </div>
                    <div style={{ fontSize: '0.68rem', color: 'var(--text-muted)' }}>
                      {formatDate(m.fecha)}
                    </div>
                  </div>
                </div>
                <div style={{
                  fontSize: '0.78rem',
                  fontWeight: 700,
                  color: m.tipo === 'ingreso' ? 'var(--success)' : 'var(--danger)',
                  whiteSpace: 'nowrap',
                  marginLeft: 8,
                }}>
                  {m.tipo === 'ingreso' ? '+' : '-'}{formatCurrency(m.monto, m.moneda)}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
